import React, { useState, useContext } from 'react';
import { LanguageContext } from '../App';
import { SearchIcon, FilterIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { pageTransition, fadeIn, slideUp, staggerContainer, listItem, buttonHover, buttonTap } from '../utils/animations';
import { useNotification } from '../hooks/useNotification';
export const Orders: React.FC = () => {
  const {
    language
  } = useContext(LanguageContext);
  const {
    toast,
    alert
  } = useNotification();
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 5;
  // Function to show order status change notifications
  const showOrderStatusNotification = (orderId: string, status: string) => {
    const getStatusInfo = () => {
      switch (status) {
        case 'in-progress':
          return {
            title: language === 'en' ? 'Order In Progress' : 'الطلب قيد التنفيذ',
            message: language === 'en' ? `Order ${orderId} is now in progress.` : `الطلب ${orderId} الآن قيد التنفيذ.`,
            type: 'info' as const
          };
        case 'delivered':
          return {
            title: language === 'en' ? 'Order Delivered' : 'تم توصيل الطلب',
            message: language === 'en' ? `Order ${orderId} has been successfully delivered.` : `تم توصيل الطلب ${orderId} بنجاح.`,
            type: 'success' as const
          };
        case 'cancelled':
          return {
            title: language === 'en' ? 'Order Cancelled' : 'تم إلغاء الطلب',
            message: language === 'en' ? `Order ${orderId} has been cancelled.` : `تم إلغاء الطلب ${orderId}.`,
            type: 'error' as const
          };
        case 'delayed':
          return {
            title: language === 'en' ? 'Order Delayed' : 'تأخر الطلب',
            message: language === 'en' ? `Order ${orderId} is experiencing a delay.` : `يواجه الطلب ${orderId} تأخيرًا.`,
            type: 'warning' as const
          };
        default:
          return {
            title: language === 'en' ? 'Order Status Updated' : 'تم تحديث حالة الطلب',
            message: language === 'en' ? `Order ${orderId} status has been updated.` : `تم تحديث حالة الطلب ${orderId}.`,
            type: 'info' as const
          };
      }
    };
    const {
      title,
      message,
      type
    } = getStatusInfo();
    toast({
      title,
      message,
      type,
      duration: 5000
    });
  };
  const orders = [{
    id: 'ORD-7829',
    customer: 'Ahmed Al-Sabah',
    pickup: 'Salmiya, Block 12',
    destination: 'Kuwait City, Block 3',
    status: 'in-progress',
    time: '10 mins ago',
    vehicle: 'Motorcycle',
    serviceType: 'Express'
  }, {
    id: 'ORD-7828',
    customer: 'Fatima Al-Qadiri',
    pickup: 'Hawally, Block 5',
    destination: 'Salmiya, Block 10',
    status: 'delivered',
    time: '25 mins ago',
    vehicle: 'Car',
    serviceType: 'Standard'
  }, {
    id: 'ORD-7827',
    customer: 'Mohammed Al-Mutairi',
    pickup: 'Jabriya, Block 1',
    destination: 'Salmiya, Block 2',
    status: 'delivered',
    time: '45 mins ago',
    vehicle: 'Motorcycle',
    serviceType: 'Express'
  }, {
    id: 'ORD-7826',
    customer: 'Sarah Al-Enezi',
    pickup: 'Kuwait City, Block 6',
    destination: 'Jabriya, Block 8',
    status: 'cancelled',
    time: '1 hour ago',
    vehicle: 'Car',
    serviceType: 'Standard'
  }, {
    id: 'ORD-7825',
    customer: 'Abdullah Al-Rashidi',
    pickup: 'Salmiya, Block 10',
    destination: 'Hawally, Block 3',
    status: 'delivered',
    time: '2 hours ago',
    vehicle: 'Motorcycle',
    serviceType: 'Express'
  }, {
    id: 'ORD-7824',
    customer: 'Noura Al-Ali',
    pickup: 'Jabriya, Block 4',
    destination: 'Kuwait City, Block 1',
    status: 'delayed',
    time: '3 hours ago',
    vehicle: 'Car',
    serviceType: 'Standard'
  }, {
    id: 'ORD-7823',
    customer: 'Khalid Al-Enezi',
    pickup: 'Hawally, Block 2',
    destination: 'Salmiya, Block 5',
    status: 'delivered',
    time: '4 hours ago',
    vehicle: 'Motorcycle',
    serviceType: 'Express'
  }, {
    id: 'ORD-7822',
    customer: 'Maryam Al-Sabah',
    pickup: 'Kuwait City, Block 8',
    destination: 'Jabriya, Block 3',
    status: 'delivered',
    time: '5 hours ago',
    vehicle: 'Car',
    serviceType: 'Standard'
  }, {
    id: 'ORD-7821',
    customer: 'Jassim Al-Mutairi',
    pickup: 'Salmiya, Block 8',
    destination: 'Hawally, Block 6',
    status: 'delivered',
    time: '6 hours ago',
    vehicle: 'Motorcycle',
    serviceType: 'Express'
  }, {
    id: 'ORD-7820',
    customer: 'Dana Al-Qadiri',
    pickup: 'Hawally, Block 9',
    destination: 'Kuwait City, Block 5',
    status: 'cancelled',
    time: '7 hours ago',
    vehicle: 'Car',
    serviceType: 'Standard'
  }];
  // Filter orders by status and search term
  const filteredOrders = orders.filter(order => (selectedStatus === 'all' || order.status === selectedStatus) && (searchTerm === '' || order.id.toLowerCase().includes(searchTerm.toLowerCase()) || order.customer.toLowerCase().includes(searchTerm.toLowerCase()) || order.pickup.toLowerCase().includes(searchTerm.toLowerCase()) || order.destination.toLowerCase().includes(searchTerm.toLowerCase())));
  // Pagination
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const getStatusClass = (status: string) => {
    switch (status) {
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'delayed':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getStatusText = (status: string) => {
    if (language === 'en') {
      switch (status) {
        case 'in-progress':
          return 'In Progress';
        case 'delivered':
          return 'Delivered';
        case 'cancelled':
          return 'Cancelled';
        case 'delayed':
          return 'Delayed';
        default:
          return 'Unknown';
      }
    } else {
      switch (status) {
        case 'in-progress':
          return 'قيد التنفيذ';
        case 'delivered':
          return 'تم التوصيل';
        case 'cancelled':
          return 'ملغي';
        case 'delayed':
          return 'متأخر';
        default:
          return 'غير معروف';
      }
    }
  };
  const handleViewOrderDetails = (order: any) => {
    showOrderStatusNotification(order.id, order.status);
  };
  return <motion.div className="space-y-6" variants={pageTransition} initial="hidden" animate="visible" exit="exit" data-id="element-1032">
      <motion.div className="flex justify-between items-center" variants={slideUp} data-id="element-1033">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-1034">
          {language === 'en' ? 'Orders' : 'الطلبات'}
        </h2>
      </motion.div>
      <motion.div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" variants={fadeIn} data-id="element-1035">
        <motion.div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0" variants={slideUp} data-id="element-1036">
          <motion.div className="relative" initial={{
          opacity: 0,
          width: '90%'
        }} animate={{
          opacity: 1,
          width: '100%'
        }} transition={{
          duration: 0.3
        }} data-id="element-1037">
            <input type="text" placeholder={language === 'en' ? 'Search orders...' : 'البحث عن طلبات...'} className="w-full sm:w-64 px-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1038" />
            <SearchIcon size={18} className="absolute left-3 top-2.5 text-gray-400" data-id="element-1039" />
          </motion.div>
          <motion.div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2" variants={staggerContainer} data-id="element-1040">
            <motion.div className="relative" variants={listItem} data-id="element-1041">
              <select className="appearance-none w-full px-4 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)} data-id="element-1042">
                <option value="all" data-id="element-1043">
                  {language === 'en' ? 'All Status' : 'جميع الحالات'}
                </option>
                <option value="in-progress" data-id="element-1044">
                  {language === 'en' ? 'In Progress' : 'قيد التنفيذ'}
                </option>
                <option value="delivered" data-id="element-1045">
                  {language === 'en' ? 'Delivered' : 'تم التوصيل'}
                </option>
                <option value="cancelled" data-id="element-1046">
                  {language === 'en' ? 'Cancelled' : 'ملغي'}
                </option>
                <option value="delayed" data-id="element-1047">
                  {language === 'en' ? 'Delayed' : 'متأخر'}
                </option>
              </select>
              <ChevronDownIcon size={16} className="absolute right-3 top-3 text-gray-400" data-id="element-1048" />
            </motion.div>
            <motion.div className="relative" variants={listItem} data-id="element-1049">
              <select className="appearance-none w-full px-4 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-1050">
                <option data-id="element-1051">
                  {language === 'en' ? 'All Areas' : 'جميع المناطق'}
                </option>
                <option data-id="element-1052">
                  {language === 'en' ? 'Kuwait City' : 'مدينة الكويت'}
                </option>
                <option data-id="element-1053">{language === 'en' ? 'Salmiya' : 'السالمية'}</option>
                <option data-id="element-1054">{language === 'en' ? 'Hawally' : 'حولي'}</option>
                <option data-id="element-1055">{language === 'en' ? 'Jabriya' : 'الجابرية'}</option>
              </select>
              <ChevronDownIcon size={16} className="absolute right-3 top-3 text-gray-400" data-id="element-1056" />
            </motion.div>
            <motion.div className="relative" variants={listItem} data-id="element-1057">
              <select className="appearance-none w-full px-4 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-1058">
                <option data-id="element-1059">{language === 'en' ? 'Today' : 'اليوم'}</option>
                <option data-id="element-1060">{language === 'en' ? 'Yesterday' : 'أمس'}</option>
                <option data-id="element-1061">
                  {language === 'en' ? 'Last 7 days' : 'آخر 7 أيام'}
                </option>
                <option data-id="element-1062">
                  {language === 'en' ? 'Last 30 days' : 'آخر 30 يوم'}
                </option>
              </select>
              <ChevronDownIcon size={16} className="absolute right-3 top-3 text-gray-400" data-id="element-1063" />
            </motion.div>
            <motion.button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" variants={listItem} whileHover={buttonHover} whileTap={buttonTap} data-id="element-1064">
              <FilterIcon size={16} className="mr-2" data-id="element-1065" />
              {language === 'en' ? 'More Filters' : 'المزيد من الفلاتر'}
            </motion.button>
          </motion.div>
        </motion.div>
        <div className="overflow-x-auto" data-id="element-1066">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1067">
            <thead className="bg-gray-50" data-id="element-1068">
              <tr data-id="element-1069">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1070">
                  {language === 'en' ? 'Order ID' : 'رقم الطلب'}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1071">
                  {language === 'en' ? 'Customer' : 'العميل'}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1072">
                  {language === 'en' ? 'Pickup Address' : 'عنوان الاستلام'}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1073">
                  {language === 'en' ? 'Destination' : 'الوجهة'}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1074">
                  {language === 'en' ? 'Service Type' : 'نوع الخدمة'}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1075">
                  {language === 'en' ? 'Status' : 'الحالة'}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1076">
                  {language === 'en' ? 'Time' : 'الوقت'}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1077">
                  {language === 'en' ? 'Vehicle' : 'المركبة'}
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1078">
                  {language === 'en' ? 'Actions' : 'الإجراءات'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1079">
              <AnimatePresence data-id="element-1080">
                {currentOrders.map((order, index) => <motion.tr key={order.id} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0,
                transition: {
                  delay: index * 0.05,
                  duration: 0.3
                }
              }} exit={{
                opacity: 0,
                y: -20
              }} whileHover={{
                backgroundColor: 'rgba(249, 250, 251, 0.5)'
              }} data-id="element-1081">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1082">
                      {order.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1083">
                      {order.customer}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1084">
                      {order.pickup}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1085">
                      {order.destination}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1086">
                      {order.serviceType}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1087">
                      <motion.span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(order.status)}`} initial={{
                    scale: 0.9
                  }} animate={{
                    scale: 1
                  }} whileHover={{
                    scale: 1.05
                  }} data-id="element-1088">
                        {getStatusText(order.status)}
                      </motion.span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1089">
                      {order.time}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1090">
                      {order.vehicle}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1091">
                      <motion.button className="text-congress-blue-600 hover:text-congress-blue-900" whileHover={{
                    scale: 1.05
                  }} whileTap={{
                    scale: 0.95
                  }} onClick={() => handleViewOrderDetails(order)} data-id="element-1092">
                        {language === 'en' ? 'View Details' : 'عرض التفاصيل'}
                      </motion.button>
                    </td>
                  </motion.tr>)}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
        <motion.div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" variants={slideUp} data-id="element-1093">
          <div className="text-sm text-gray-700" data-id="element-1094">
            {language === 'en' ? `Showing ${indexOfFirstOrder + 1} to ${Math.min(indexOfLastOrder, filteredOrders.length)} of ${filteredOrders.length} results` : `عرض ${indexOfFirstOrder + 1} إلى ${Math.min(indexOfLastOrder, filteredOrders.length)} من ${filteredOrders.length} نتيجة`}
          </div>
          <div className="flex space-x-2" data-id="element-1095">
            <motion.button className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} whileHover={buttonHover} whileTap={buttonTap} data-id="element-1096">
              <ChevronLeftIcon size={16} className="mr-1" data-id="element-1097" />
              {language === 'en' ? 'Previous' : 'السابق'}
            </motion.button>
            <motion.button className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages || totalPages === 0} whileHover={buttonHover} whileTap={buttonTap} data-id="element-1098">
              {language === 'en' ? 'Next' : 'التالي'}
              <ChevronRightIcon size={16} className="ml-1" data-id="element-1099" />
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>;
};